import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api, uploadUrl } from '../api';
import { isNative, takePhoto, pickPhoto } from '../native';

export default function ImageManager({ productId, images = [], onImagesChange }) {
  const { t } = useTranslation();
  const [uploading, setUploading]   = useState(false);
  const [error, setError]           = useState('');
  const cameraRef  = useRef(null);
  const libraryRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setError('');

    if (!productId) {
      const reader = new FileReader();
      reader.onload = () => onImagesChange({ type: 'local', file, preview: reader.result });
      reader.readAsDataURL(file);
      return;
    }

    setUploading(true);
    try {
      const img = await api.uploadImage(productId, file);
      onImagesChange({ type: 'uploaded', image: img });
    } catch {
      setError(t('image.uploadFailed'));
    } finally {
      setUploading(false);
      if (cameraRef.current) cameraRef.current.value = '';
      if (libraryRef.current) libraryRef.current.value = '';
    }
  };

  const handleNativePhoto = async (type) => {
    const result = type === 'camera' ? await takePhoto() : await pickPhoto();
    if (!result) return;
    const { blob } = result;
    const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
    await handleFile(file);
  };

  const deleteImage = async (img) => {
    if (img.local) { onImagesChange({ type: 'delete-local' }); return; }
    try {
      await api.deleteImage(img.id);
      onImagesChange({ type: 'delete', id: img.id });
    } catch { setError('Kunne ikke slette billedet'); }
  };

  const btnStyle = {
    flex: 1, padding: '12px 0', borderRadius: 12, border: '1px solid var(--border-subtle)',
    background: 'var(--bg-elevated)', color: 'oklch(60% 0.17 230)',
    fontSize: 14, fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
    cursor: 'pointer', textAlign: 'center',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Billede-grid */}
      {images.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {images.map((img, i) => (
            <div key={img.id || `local-${i}`} style={{ position: 'relative', aspectRatio: '1', borderRadius: 12, overflow: 'hidden', background: 'var(--bg-elevated)' }}>
              <img src={img.preview || uploadUrl(img.filename)} alt={img.label || ''}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              {img.label && (
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)', fontSize: 11, color: 'white', padding: '4px 8px', truncate: true }}>
                  {img.label}
                </div>
              )}
              <button type="button" onClick={() => deleteImage(img)}
                style={{ position: 'absolute', top: 6, right: 6, width: 28, height: 28, borderRadius: '50%', background: 'var(--status-expired)', border: 'none', color: 'white', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* Upload-knapper */}
      {uploading ? (
        <div style={{ ...btnStyle, color: 'var(--fg-4)', opacity: 0.7 }}>⏳ {t('image.uploading')}</div>
      ) : isNative() ? (
        // Native: brug Capacitor Camera API
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => handleNativePhoto('camera')} style={btnStyle}>📷 Kamera</button>
          <button type="button" onClick={() => handleNativePhoto('library')} style={btnStyle}>🖼 Bibliotek</button>
        </div>
      ) : (
        // Web: brug file input
        <div style={{ display: 'flex', gap: 8 }}>
          <label style={{ flex: 1, cursor: 'pointer' }}>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment"
              className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
            <div style={btnStyle}>📷 Kamera</div>
          </label>
          <label style={{ flex: 1, cursor: 'pointer' }}>
            <input ref={libraryRef} type="file" accept="image/*"
              className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
            <div style={btnStyle}>🖼 Bibliotek</div>
          </label>
        </div>
      )}

      {error && (
        <p style={{ fontSize: 13, color: 'var(--status-expired)', textAlign: 'center', background: 'oklch(55% 0.22 25 / 12%)', borderRadius: 10, padding: '8px 12px', fontFamily: 'DM Sans, sans-serif' }}>{error}</p>
      )}
    </div>
  );
}
